'use client';

import React from 'react';
import { WorkspaceLink } from '@/app/features/workspace/routing/WorkspaceLink';
import { useLocationPathname, useLocationSearchParams } from '@/app/features/workspace/routing/useWorkspacePath';
import { useSelector } from 'react-redux';
import { Home } from 'lucide-react';
import type { RootState } from '@/app/store';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { useTranslation } from '@/app/features/language/hooks/useTranslation';
import { workspaceHref } from '@/app/features/workspace/utils/workspaceRoute';
import { resolveAppContext } from '@/app/features/workspace/routing/viewRegistry';
import { resolveAppNav } from '@/app/features/workspace/routing/appNavRegistry';

/**
 * Static route-to-i18n-key map.
 * Each key is a route segment, value is the i18n key under the "breadcrumb" namespace.
 */
const ROUTE_LABEL_MAP: Record<string, string> = {
  // Admin area
  admin: 'admin',
  users: 'users',
  roles: 'roles',
  'api-keys': 'apiKeys',
  apps: 'apps',
  tenants: 'tenants',
  configuration: 'configuration',
  'code-lists': 'codeLists',
  'profile-pictures': 'profilePictures',
  notifications: 'notifications',
  send: 'sendNotification',
  // Workspace area
  workspace: 'workspace',
  settings: 'settings',
  parties: 'parties',
  'party-definitions': 'partyDefinitions',
  branding: 'branding',
  'item-master': 'itemMaster',
  'uom-ladders': 'uomLadders',
  'packaging-rules': 'packagingRules',
  'carton-types': 'cartonTypes',
  'approved-cartons': 'approvedCartons',
  'edi-archive': 'ediArchive',
  'key-composition': 'keyComposition',
  'pack-analysis': 'packAnalysis',
  mlinkone: 'mlinkone',
  // EDI Admin (spec: user-party-assignment)
  'party-members': 'partyAssignments',
  'affiliation-types': 'affiliationTypes',
  'edi-roles': 'ediRoles',
  'edi-permissions': 'ediPermissions',
  'trading-partner-enrollments': 'tradingPartnerEnrollments',
  'active-party-defaults': 'activePartyDefaults',
  // Main area (public landing shell)
  main: 'main',
  // Tools area
  tools: 'tools',
  'simple-notes': 'simpleNotes',
  'weather-forecast': 'weatherForecast',
  'barcode-generator': 'barcodeGenerator',
};

/**
 * Routes that have an actual page.tsx — only these are rendered as clickable links.
 */
const NAVIGABLE_ROUTES = new Set([
  '/admin',
  '/admin/users',
  '/admin/roles',
  '/admin/api-keys',
  '/admin/apps',
  '/admin/tenants',
  '/admin/configuration',
  '/admin/code-lists',
  '/admin/profile-pictures',
  '/admin/notifications/send',
  '/workspace',
  '/workspace/settings/parties',
  '/workspace/settings/party-definitions',
  '/workspace/settings/branding',
  '/workspace/settings/item-master',
  '/workspace/settings/uom-ladders',
  '/workspace/settings/packaging-rules',
  '/workspace/settings/carton-types',
  '/workspace/settings/approved-cartons',
  '/workspace/settings/key-composition',
  '/workspace/settings/party-members',
  '/workspace/settings/affiliation-types',
  '/workspace/settings/edi-roles',
  '/workspace/settings/edi-permissions',
  '/workspace/settings/trading-partner-enrollments',
  '/workspace/settings/active-party-defaults',
  '/workspace/apps/pack-analysis',
  '/workspace/apps/mlinkone',
  '/workspace/tools/simple-notes',
  '/workspace/tools/weather-forecast',
  '/workspace/tools/barcode-generator',
  '/main',
  '/main/tools/simple-notes',
  '/main/tools/weather-forecast',
  '/main/tools/barcode-generator',
  '/tools/simple-notes',
  '/tools/weather-forecast',
  '/tools/barcode-generator',
]);

interface BreadcrumbSegment {
  label: string;
  href: string;
  navigable: boolean;
}

const AppBreadcrumb: React.FC = React.memo(() => {
  // Live URL (static-export safe) — `usePathname()` returns the baked shell
  // route under `output: 'export'`. Spec: workspace-static-spa-routing.
  const pathname = useLocationPathname();
  // Live search params for the same reason as the pathname above: History-API navigation inside
  // the shell never notifies Next's router, so `useSearchParams()` returns empty here.
  const appParam = useLocationSearchParams().get('app');
  const { t } = useTranslation('breadcrumb');
  // app-scoped-left-nav: trailing in-app section crumb is labeled from the app
  // descriptor (per-app naming) under the `appNav` namespace.
  const { t: tAppNav } = useTranslation('appNav');
  const activeTenant = useSelector((state: RootState) => state.tenant.activeTenant);
  // Same source the page heading and left nav resolve the app name from, so the three
  // surfaces can never disagree about which app the user is looking at.
  const workspaceApps = useSelector((state: RootState) => state.workspace.apps);
  const homeHref = activeTenant ? workspaceHref(activeTenant) : '/main';

  const rawSegments = pathname.split('/').filter(Boolean);

  // Only show breadcrumb at depth 2+
  if (rawSegments.length < 2) {
    return null;
  }

  // Admin panel has its own top-level tab navigation — showing a breadcrumb
  // there is redundant and competes visually with the tabs. Big-tech admin
  // surfaces (Shopify admin, GitHub settings, Linear settings) never
  // breadcrumb their own tabbed nav. Hide on any /admin/* route.
  if (rawSegments[0] === 'admin') {
    return null;
  }

  // Workspace routes carry a [tenantCode] segment right after `workspace`
  // (spec: workspace-tenant-code-routing). The code is part of the URL but not
  // a meaningful crumb — drop it from the display, and match the code-less
  // form against ROUTE_LABEL_MAP / NAVIGABLE_ROUTES while re-inserting the code
  // into each crumb's actual href.
  const tenantCode =
    rawSegments[0] === 'workspace' && rawSegments.length >= 2 ? rawSegments[1] : null;
  const segments = tenantCode
    ? [rawSegments[0], ...rawSegments.slice(2)]
    : rawSegments;
  const withTenantCode = (codelessHref: string): string =>
    tenantCode
      ? codelessHref.replace(/^\/workspace/, `/workspace/${tenantCode}`)
      : codelessHref;

  // Build breadcrumb segments
  const breadcrumbs: BreadcrumbSegment[] = segments.map((segment, index) => {
    const codelessHref = '/' + segments.slice(0, index + 1).join('/');
    const href = withTenantCode(codelessHref);
    const mapped = ROUTE_LABEL_MAP[segment];
    // Kebab-case → Title Case fallback (also used for unmapped app-section
    // segments like `master-data`, so they read cleanly without a locale key).
    const titleCase = segment
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    let label: string;
    if (mapped) {
      try {
        label = t(mapped);
      } catch {
        label = titleCase;
      }
    } else {
      label = titleCase;
    }
    const navigable = NAVIGABLE_ROUTES.has(codelessHref);
    return { label, href, navigable };
  });

  // app-scoped-left-nav: when inside an app section, label the trailing crumb
  // from the app descriptor (per-app naming, e.g. "Pack Planner", "PO Browser").
  if (tenantCode && breadcrumbs.length > 0) {
    const ctx = resolveAppContext(rawSegments.slice(2).join('/'));
    const descriptor = resolveAppNav(ctx.appKey);
    if (descriptor && ctx.sectionKey) {
      const item = descriptor.groups
        .flatMap((g) => g.items)
        .find((i) => i.sectionKey === ctx.sectionKey);
      if (item) {
        const last = breadcrumbs.length - 1;
        breadcrumbs[last] = { ...breadcrumbs[last], label: tAppNav(item.labelKey) };
      }
    }
  }

  // ⚠ The `?app=` alias is part of a view's IDENTITY, not decoration: two SysApps can share
  // one pathname and differ only by it — Business Notes is `…/tools/simple-notes?app=business-notes`
  // while Simple Notes is `…/tools/simple-notes`. Labelling the trailing crumb from the path
  // segment alone therefore named the WRONG app ("Simple Notes" while the heading and left-nav
  // both read "Business Notes"). Resolved from `state.workspace.apps` — the same source the page
  // heading and the left nav use, so all three can never disagree.
  if (appParam && breadcrumbs.length > 0) {
    const aliasName = workspaceApps.find((a) => a.appCode === appParam)?.appName;
    if (aliasName) {
      const last = breadcrumbs.length - 1;
      breadcrumbs[last] = {
        ...breadcrumbs[last],
        // Keep the alias on the href so the crumb links back to THIS app, not its path-twin.
        href: `${breadcrumbs[last].href}?app=${appParam}`,
        label: aliasName,
      };
    }
  }

  // Break out of the Layout's max-w-6xl container so the breadcrumb stays
  // flush with the left edge of the viewport (matching the AppShell below it).
  // Left padding aligned to AppShell activity bar icon left edge (~18px).
  const breakoutStyle: React.CSSProperties = {
    marginLeft: 'calc(50% - 50vw)',
    marginRight: 'calc(50% - 50vw)',
    width: '100vw',
    maxWidth: '100vw',
  };

  return (
    <div className="pt-3 pb-1 pl-[18px] -mb-3" style={breakoutStyle}>
      <Breadcrumb>
        <BreadcrumbList className="justify-start">
          {/* Home icon */}
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <WorkspaceLink href={homeHref} aria-label="Home">
                <Home className="h-5 w-5" />
              </WorkspaceLink>
            </BreadcrumbLink>
          </BreadcrumbItem>

          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            return (
              <React.Fragment key={crumb.href}>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  {isLast ? (
                    <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                  ) : crumb.navigable ? (
                    <BreadcrumbLink asChild>
                      <WorkspaceLink href={crumb.href}>{crumb.label}</WorkspaceLink>
                    </BreadcrumbLink>
                  ) : (
                    <span className="text-muted-foreground">{crumb.label}</span>
                  )}
                </BreadcrumbItem>
              </React.Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  );
});

AppBreadcrumb.displayName = 'AppBreadcrumb';

export default AppBreadcrumb;
